import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Search,
  Star,
  ChevronRight,
  Utensils,
  Tag,
  X,
  Plus,
  Minus,
  SparkleIcon,
  Sparkle,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { MoreVertical, BookOpen } from 'lucide-react';
import { FaMartiniGlassCitrus } from 'react-icons/fa6';
import { GiCook } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContex';
import { fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';
import HistoryPage from './orderhistory';

const VenuePage = () => {
  // State management
  let {
    cartItems,
    menuData,
    clearCart,
    handleAddItem: contextAddItem,
    handleDecreaseItem: contextDecreaseItem,
    updateMenuData,
  } = useCart();
  const [venueDetails, setVenueDetails] = useState({});
  const [offersData, setOffersData] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [activeTab, setActiveTab] = useState('food');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedBySearch, setExpandedBySearch] = useState({});
  const [activeFilters, setActiveFilters] = useState({
    veg: false,
    nonVeg: false,
    bestSeller: false,
    chefsSpecial: false,
  });
  const [customizingItem, setCustomizingItem] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({
    variations: null,
    addons: [],
    customizations: {},
  });

  // Refs
  const categoryRefs = useRef({
    food: {},
    drinks: {},
    specials: {}, // Add if you have specials
  });
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const HistoryIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  );
  // Data fetching
  const fetchData = async () => {
    try {
      const venueId = localStorage.getItem('venueId');
      const token = localStorage.getItem('tringAuthToken');

      const venueResponse = await fetchWithAuth(
        API_ENDPOINTS.VENUE_BY_ID(venueId),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setVenueDetails(await venueResponse.json());

      const offersResponse = await fetchWithAuth(
        `${API_ENDPOINTS.VENUE_OFFERS(venueId)}?limit=100`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOffersData((await offersResponse.json()).offers || []);

      const menuResponse = await fetchWithAuth(
        API_ENDPOINTS.VENUE_MENU(venueId),
        { method: 'GET' }
      );
      const menuData = await menuResponse.json();

      const getMenuByType = (menu, type) => {
        return (
          menu.find(m => m.menuType?.toLowerCase() === type.toLowerCase()) ||
          null
        );
      };

      updateMenuData({
        food: getMenuByType(menuData, 'food'),
        drinks: getMenuByType(menuData, 'bar'),
        specials: getMenuByType(menuData, 'specials'),
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      updateMenuData({ food: null, drinks: null });
    }
  };

  useEffect(() => {
    const venueId = localStorage.getItem('venueId');
    const storedCartId = sessionStorage.getItem('cartId');

    // Reset cart if venue changed
    if (storedCartId && storedCartId !== venueId) {
      clearCart();
    }

    // Set current venue as cart ID
    sessionStorage.setItem('cartId', venueId);

    fetchData();
  }, [clearCart]);

  // Search functionality
  const searchMenuItems = query => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const getSearchableItems = () => {
      if (!menuData[activeTab]?.categories) return [];
      return menuData[activeTab].categories.flatMap(
        category =>
          category.items?.map(item => ({
            ...item,
            categoryName: category.name,
          })) || []
      );
    };

    const results = getSearchableItems().filter(item =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );

    // Apply activeFilters to the search results
    const filteredResults = applyFilters(results);

    setSearchResults(filteredResults);
  };

  useEffect(() => {
    const timer = setTimeout(() => searchMenuItems(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, menuData]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setExpandedBySearch({});
      return;
    }
    const q = searchQuery.toLowerCase();
    const newExpanded = {};
    (menuData[activeTab]?.categories || []).forEach(cat => {
      const nameMatches = cat.name.toLowerCase().includes(q);
      const itemMatches = cat.items?.some(item =>
        item.name.toLowerCase().includes(q)
      );
      if (nameMatches || itemMatches) {
        newExpanded[cat.id] = true;
      }
    });
    setExpandedBySearch(newExpanded);
  }, [searchQuery, activeTab, menuData]);

  const toggleCategory = categoryId => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Determine if category should be expanded
  const isCategoryExpanded = categoryId => {
    // If expanded by search, return true
    if (expandedBySearch[categoryId]) {
      return true;
    }
    // Return user's setting if exists, otherwise default to true
    return expandedCategories[categoryId] ?? true;
  };

  // Cart helpers
  const getCartItemsForBaseId = baseId =>
    cartItems.filter(item => item.baseId === baseId);

  const getTotalQuantity = baseId =>
    getCartItemsForBaseId(baseId).reduce((sum, item) => sum + item.quantity, 0);

  // Item handlers
  const handleItemClick = item => {
    if (
      item.variations?.length > 0 ||
      item.addons?.length > 0 ||
      item.customizations?.length > 0
    ) {
      setCustomizingItem(item);
      setSelectedOptions({ variations: null, addons: [], customizations: {} });
    } else {
      contextAddItem(item);
    }
  };

  const handleAddCustomizedItem = () => {
    if (!customizingItem) return;

    const { variations, addons, customizations } = selectedOptions;
    // Use the price from variation or base item
    const price = variations?.price || customizingItem.price;

    contextAddItem(customizingItem, {
      variations,
      addons,
      customizations,
      price,
    });

    setCustomizingItem(null);
  };

  // Filter helpers
  const toggleFilter = filter => {
    setActiveFilters(prev => ({
      ...prev,
      veg: filter === 'veg' ? !prev.veg : false,
      nonVeg: filter === 'nonVeg' ? !prev.nonVeg : false,
      [filter]: ['veg', 'nonVeg'].includes(filter)
        ? !prev[filter]
        : !prev[filter],
    }));
  };

  const applyFilters = items =>
    items.filter(item => {
      if (activeFilters.veg && item.itemType !== '1') return false;
      if (
        activeFilters.nonVeg &&
        item.itemType !== '2' &&
        item.itemType !== '3'
      )
        return false;
      if (activeFilters.bestSeller && !item.bestSeller) return false;
      if (activeFilters.chefsSpecial && !item.chefsSpecial) return false;
      return true;
    });

  // Effects
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Clear refs when menu data changes
    categoryRefs.current = {
      food: {},
      drinks: {},
      specials: {},
    };
  }, [menuData]);

  useEffect(() => {
    document.body.style.overflow =
      isMenuOpen || customizingItem ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen, customizingItem]);

  useEffect(() => {
    const handleScroll = () => setScrollPosition(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Render helpers
  const renderMenuItems = items => {
    const filteredItems = applyFilters(items);

    return filteredItems.map(item => {
      const baseId = item._id || item.id;
      const totalQuantity = getTotalQuantity(baseId);
      const isCustomizable =
        item.variations?.length > 0 ||
        item.addons?.length > 0 ||
        item.customizations?.length > 0;

      return (
        <div key={baseId} style={styles.menuItem}>
          <div style={styles.itemInfo}>
            {activeTab !== 'drinks' && item.itemType ? (
              <div style={styles.itemTypeIndicator}>
                {item.itemType === '1' ? (
                  <div style={styles.vegIndicatorContainer}>
                    <div style={styles.vegIndicatorInner} />
                  </div>
                ) : (
                  <div style={styles.nonVegIndicatorContainer}>
                    <div style={styles.nonVegIndicatorInner} />
                  </div>
                )}
              </div>
            ) : (
              // Drink item indicator
              <div style={styles.drinkDotContainer}>
                <div style={styles.drinkDot} />
              </div>
            )}
            <div style={styles.itemDetails}>
              <h3 style={styles.itemName}>{item.name}</h3>

              {/* NEW: Price and tags container */}
              <div style={styles.priceTagsContainer}>
                <p style={styles.itemPrice}>₹{item.price}</p>
                <div style={styles.tagsContainer}>
                  {/* Enhanced floating tags */}
                  <div style={styles.floatingTagsContainer}>
                    {item.bestSeller && (
                      <div style={styles.enhancedBestsellerTag}>
                        <div style={styles.tagGlow} />
                        <Star size={12} fill="white" color="white" />
                        <span style={{ fontSize: '9px' }}>Bestseller</span>
                        <div style={styles.tagShine} />
                      </div>
                    )}
                    {item.chefsSpecial && (
                      <div style={styles.enhancedChefsSpecialTag}>
                        <div style={styles.tagGlow} />
                        <GiCook size={12} color="white" />
                        <span style={styles.tagText}>Chef's Special</span>
                        <div style={styles.tagShine} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={styles.itemActions}>
            {totalQuantity > 0 ? (
              <div style={styles.quantityControl}>
                <Minus
                  size={16}
                  onClick={e => {
                    e.stopPropagation();
                    const items = getCartItemsForBaseId(baseId);
                    if (items.length > 0) contextDecreaseItem(items[0].cartId);
                  }}
                  style={styles.quantityButton}
                />
                <span>{totalQuantity}</span>
                <Plus
                  size={16}
                  onClick={() => handleItemClick(item)}
                  style={styles.quantityButton}
                />
              </div>
            ) : (
              <button
                onClick={() => handleItemClick(item)}
                style={styles.addButton}
              >
                ADD
              </button>
            )}
            {isCustomizable && (
              <div style={styles.customizableLabel}>Customisable</div>
            )}
          </div>
        </div>
      );
    });
  };

  const renderMenuCategories = menuType => {
    const cats = menuData[menuType]?.categories;
    if (!cats) {
      return <p style={styles.noMenuText}>No {menuType} menu available</p>;
    }

    return cats
      .map(category => {
        const q = searchQuery.toLowerCase();

        // If category name matches, show all items
        const nameMatches = category.name.toLowerCase().includes(q);

        // Otherwise filter by item name
        const searchFilteredItems = nameMatches
          ? category.items
          : (category.items || []).filter(item =>
              item.name.toLowerCase().includes(q)
            );

        // Apply activeFilters to these items to see if we should show the category
        const finalItems = applyFilters(searchFilteredItems);

        // If nothing to show, skip this category
        if (finalItems.length === 0) {
          return null;
        }

        return (
          <div
            key={category.id}
            style={styles.categoryContainer}
            ref={el => (categoryRefs.current[menuType][category.id] = el)}
          >
            <h2
              style={styles.categoryTitle}
              onClick={() => toggleCategory(category.id)}
            >
              {category.name}
              <span style={styles.arrowIcon}>
                <ChevronDown
                  size={16}
                  style={{
                    transform: isCategoryExpanded(category.id)
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </span>
            </h2>

            {isCategoryExpanded(category.id) &&
              renderMenuItems(searchFilteredItems)}
          </div>
        );
      })
      .filter(Boolean);
  };

  // Calculate cart totals
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      // searchMenuItems(searchQuery);
    }
  };
  const scrollToCategory = categoryId => {
    setIsMenuOpen(false);

    setTimeout(() => {
      try {
        const element = categoryRefs.current[activeTab]?.[categoryId];
        if (!element) {
          console.warn(`Category element not found: ${categoryId}`);
          return;
        }

        const headerHeight =
          document.querySelector('[style*="position: sticky"]')?.offsetHeight ||
          180;

        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;

        // Add manual offset (positive or negative)
        const manualOffset = 40; // Adjust this value as needed
        const scrollPosition = absoluteElementTop - headerHeight - manualOffset;

        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });

        if (!isCategoryExpanded(categoryId)) {
          toggleCategory(categoryId);
        }
      } catch (error) {
        console.error('Scroll error:', error);
      }
    }, 100);
  };

  return (
    <div style={styles.container}>
      {/* Search and Filters */}
      <div style={styles.searchContainer}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <ArrowLeft
              size={24}
              onClick={() => window.history.back()}
              style={styles.backButton}
            />
            <h1 style={styles.venueName}>{venueDetails.name}</h1>
            <div
              style={styles.ratingBadge}
              onClick={() => navigate('/share?scrollToRatings=1')}
            >
              <span style={styles.ratingText}>
                {venueDetails.rating ? venueDetails.rating.toFixed(1) : '0.0'}
              </span>
              <Star size={12} fill="white" />
            </div>
          </div>
        </div>
        {activeTab !== 'offers' && activeTab !== 'history' && (
          <div style={styles.searchBar}>
            <Search size={20} color="#888" />
            <input
              ref={searchInputRef}
              placeholder={
                activeTab === 'drinks'
                  ? 'Search for drinks'
                  : 'Search for dishes'
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              style={styles.searchInput}
            />
            {searchQuery && (
              <X
                size={20}
                color="#888"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  searchInputRef.current.focus();
                }}
                style={styles.clearSearch}
              />
            )}
          </div>
        )}
        {activeTab === 'food' && (
          <div style={styles.filtersContainer}>
            {/* Veg/Non-Veg Toggle */}
            <div
              onClick={() => toggleFilter('veg')}
              style={{
                width: 60,
                height: 30,
                backgroundColor: 'white',
                border: '1px solid #E0E0E0',
                borderRadius: 16,
                padding: 3,
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 6,
                  backgroundColor: activeFilters.veg ? '#4CAF50' : '#E0E0E0',
                  borderRadius: '19px',
                  position: 'absolute',
                  top: 11,
                  marginLeft: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: '2px solid #4CAF50',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    position: 'absolute',
                    left: activeFilters.veg ? 26 : 1,
                    transition: 'left 0.25s ease',
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      backgroundColor: '#4CAF50',
                      borderRadius: '50%',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Non-Veg Toggle */}
            <div
              onClick={() => toggleFilter('nonVeg')}
              style={{
                width: 60,
                height: 30,
                backgroundColor: 'white',
                borderRadius: 20,
                padding: 4,
                cursor: 'pointer',
                border: '1px solid #E0E0E0',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 6,
                  backgroundColor: activeFilters.nonVeg ? '#D32F2F' : '#E0E0E0',
                  borderRadius: '19px',
                  position: 'absolute',
                  top: 11,
                  marginLeft: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: '2px solid #D32F2F',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    position: 'absolute',
                    left: activeFilters.nonVeg ? 26 : 1,
                    transition: 'left 0.25s ease',
                  }}
                >
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: '5px solid transparent',
                      borderRight: '5px solid transparent',
                      borderBottom: '9px solid #D32F2F',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Other Filters */}
            <button
              onClick={() => toggleFilter('bestSeller')}
              style={styles.filterButton(activeFilters.bestSeller, '#FF9800')}
            >
              <Star
                size={14}
                fill={activeFilters.bestSeller ? 'white' : 'orange'}
              />
              Bestseller
            </button>
            {/* Chef's Special Button */}
            <button
              onClick={() => toggleFilter('chefsSpecial')}
              style={{
                padding: '4px 7px',
                borderRadius: '20px',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                fontWeight: '500',
                background: activeFilters.chefsSpecial
                  ? 'linear-gradient(135deg, #8A2BE2 0%, #9966CC 100%)'
                  : 'white',
                color: activeFilters.chefsSpecial ? 'white' : '#8A2BE2',
                border: activeFilters.chefsSpecial
                  ? 'none'
                  : '1px solid #8A2BE2',
                whiteSpace: 'nowrap',
              }}
            >
              <GiCook
                size={14}
                color={activeFilters.chefsSpecial ? 'white' : '#8A2BE2'}
              />
              Chef's Special
            </button>
          </div>
        )}
      </div>
      {/* Menu Content */}
      {activeTab !== 'history' && (
        <div style={styles.menuContent}>
          {activeTab === 'food' && renderMenuCategories('food')}
          {activeTab === 'drinks' && renderMenuCategories('drinks')}
          {activeTab === 'specials' && menuData.specials && (
            <div style={styles.specialMenuContainer}>
              {renderMenuCategories('specials')}
            </div>
          )}
          {activeTab === 'offers' && (
            <div style={styles.offersContainer}>
              {offersData.length > 0 ? (
                <>
                  <h2 style={styles.offersTitle}>Available Offers</h2>
                  {offersData.map(offer => (
                    <div key={offer.id} style={styles.offerCard}>
                      <div style={styles.offerHeader}>
                        <h3 style={styles.offerTitle}>
                          <Tag size={18} />
                          {offer.title}
                        </h3>
                      </div>
                      <div style={styles.offerBody}>
                        <p style={styles.offerDescription}>
                          {offer.description}
                        </p>
                        <div style={styles.offerDetails}>
                          <span style={styles.offerValidUntil}>
                            Valid Until:{' '}
                            {new Date(offer.validTo).toLocaleDateString(
                              'en-US',
                              {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              }
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div style={styles.noOffers}>
                  <Tag size={48} color="#999" />
                  <p style={styles.noOffersText}>No offers available</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Cart Summary */}
      {cartItems.length > 0 && (
        <div style={styles.cartSummary}>
          <div>
            <span style={styles.itemCount}>
              {totalItems} {totalItems === 1 ? 'item' : 'items'}
            </span>
          </div>
          <div style={styles.viewOrderButton} onClick={() => navigate('/cart')}>
            <span style={styles.viewOrderText}>View Order</span>
            <ChevronRight size={18} />
          </div>
        </div>
      )}
      {/* Menu Categories Button */}
      {activeTab !== 'offers' && activeTab !== 'history' && (
        <div
          onClick={() => setIsMenuOpen(true)}
          style={{
            position: 'fixed',
            bottom: cartItems.length > 0 ? '120px' : '70px',
            right: '20px',
            background:
              'linear-gradient(90deg, #CC151E -18.35%, #873662 108.23%)',
            borderRadius: '50%',
            width: '62px',
            height: '62px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            cursor: 'pointer',
            zIndex: 1002,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          {' '}
          <img
            alt=""
            style={{
              height: '35px',
              width: '35px',
              color: 'white',
              fill: 'white',
            }}
            src="./images/menu (1).svg"
          />
          <span style={styles.menuButtonText}>MENU</span>
        </div>
      )}

      {activeTab === 'history' && <HistoryPage />}
      {/* Categories Menu */}
      {isMenuOpen && (
        <div style={styles.categoriesMenu}>
          <div style={styles.categoriesMenuContent}>
            <div style={styles.categoriesMenuHeader}>
              <h2 style={styles.categoriesMenuTitle}>
                {activeTab === 'food' ? 'Food Menu' : 'Bar Menu'}
              </h2>
              <X
                size={24}
                onClick={() => setIsMenuOpen(false)}
                color="#666"
                style={styles.closeCategoriesMenu}
              />
            </div>
            <div style={styles.categoriesList}>
              {(activeTab === 'food'
                ? menuData.food?.categories || []
                : menuData.drinks?.categories || []
              ).map(category => (
                <div
                  key={category.id}
                  style={styles.categoryMenuItem}
                  onClick={() => {
                    scrollToCategory(category.id);
                  }}
                >
                  <span>{category.name}</span>
                  <span style={styles.categoryItemCount}>
                    {category.items?.length || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Customization Modal */}
      {customizingItem && (
        <div style={styles.customizationModal}>
          <div style={styles.customizationContent}>
            <div style={styles.customizationHeader}>
              <h2 style={styles.customizationTitle}>
                Customize {customizingItem.name}
              </h2>
              <X
                size={24}
                onClick={() => setCustomizingItem(null)}
                style={styles.closeCustomization}
              />
            </div>

            <div style={styles.customizationOptions}>
              {/* Variations */}
              {customizingItem.variations?.length > 0 && (
                <div style={styles.optionGroup}>
                  <h4 style={styles.optionGroupTitle}>Select Size</h4>
                  {customizingItem.variations.map(variation => (
                    <div
                      key={variation.id}
                      style={styles.optionItem(
                        selectedOptions.variations?.id === variation.id
                      )}
                      onClick={() =>
                        setSelectedOptions(prev => ({
                          ...prev,
                          variations: variation,
                        }))
                      }
                    >
                      <div style={styles.optionInfo}>
                        <span style={styles.optionName}>{variation.name}</span>
                        <span style={styles.optionPrice}>
                          ₹{variation.price}
                        </span>
                      </div>
                      {selectedOptions.variations?.id === variation.id && (
                        <div style={styles.optionSelectedIndicator}>
                          <div style={styles.optionSelectedDot} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Addons */}
              {customizingItem.addons?.length > 0 && (
                <div style={styles.optionGroup}>
                  <h4 style={styles.optionGroupTitle}>Add Extra</h4>
                  {customizingItem.addons.map(addon => (
                    <div
                      key={addon.id}
                      style={styles.optionItem(
                        selectedOptions.addons.some(a => a.id === addon.id)
                      )}
                      onClick={() => {
                        setSelectedOptions(prev => ({
                          ...prev,
                          addons: prev.addons.some(a => a.id === addon.id)
                            ? prev.addons.filter(a => a.id !== addon.id)
                            : [...prev.addons, addon],
                        }));
                      }}
                    >
                      <div style={styles.optionInfo}>
                        <span style={styles.optionName}>{addon.name}</span>
                        <span style={styles.optionPrice}>₹{addon.price}</span>
                      </div>
                      {selectedOptions.addons.some(a => a.id === addon.id) && (
                        <div style={styles.optionCheckboxSelected}>✓</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Customizations */}
              {customizingItem.customizations?.length > 0 && (
                <div style={styles.optionGroup}>
                  {customizingItem.customizations.map(group => (
                    <div key={group.id} style={styles.customizationGroup}>
                      <h4 style={styles.optionGroupTitle}>{group.name}</h4>
                      {group.options.map(option => (
                        <div
                          key={option.id || option.name}
                          style={styles.optionItem(
                            selectedOptions.customizations[group.id]?.name ===
                              option.name
                          )}
                          onClick={() =>
                            setSelectedOptions(prev => ({
                              ...prev,
                              customizations: {
                                ...prev.customizations,
                                [group.id]: option,
                              },
                            }))
                          }
                        >
                          <div style={styles.optionInfo}>
                            <span style={styles.optionName}>{option.name}</span>
                            {option.price > 0 && (
                              <span style={styles.optionPrice}>
                                ₹{option.price}
                              </span>
                            )}
                          </div>
                          {selectedOptions.customizations[group.id]?.name ===
                            option.name && (
                            <div style={styles.optionSelectedIndicator}>
                              <div style={styles.optionSelectedDot} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.customizationFooter}>
              <div>
                <div style={styles.totalLabel}>Total</div>
                <div style={styles.totalAmount}>
                  ₹
                  {(
                    Object.values(selectedOptions.customizations).reduce(
                      (sum, option) => sum + (option.price || 0),
                      0
                    ) ||
                    (selectedOptions.variations?.price ||
                      customizingItem.price) +
                      selectedOptions.addons.reduce(
                        (sum, addon) => sum + addon.price,
                        0
                      )
                  ).toFixed(2)}
                </div>
              </div>
              <button
                onClick={handleAddCustomizedItem}
                style={styles.addToCartButton}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bottom Navigation */}
      <div style={styles.bottomNav}>
        <button
          onClick={() => setActiveTab('food')}
          style={styles.navButton(activeTab === 'food')}
        >
          <Utensils
            size={20}
            color={activeTab === 'food' ? '#CC151E' : '#666'}
          />
          <span style={styles.navButtonText(activeTab === 'food')}>Food</span>
        </button>
        <button
          onClick={() => setActiveTab('drinks')}
          style={styles.navButton(activeTab === 'drinks')}
        >
          <FaMartiniGlassCitrus
            size={20}
            color={activeTab === 'drinks' ? '#CC151E' : '#666'}
            style={{ marginLeft: '4px' }}
          />
          <span style={styles.navButtonText(activeTab === 'drinks')}>Bar</span>
        </button>
        {menuData.specials && (
          <button
            onClick={() => setActiveTab('specials')}
            style={styles.navButton(activeTab === 'specials')}
          >
            <Sparkles
              size={20}
              color={activeTab === 'specials' ? '#CC151E' : '#666'}
            />
            <span style={styles.navButtonText(activeTab === 'specials')}>
              Special
            </span>
          </button>
        )}
        <button
          onClick={() => setActiveTab('offers')}
          style={styles.navButton(activeTab === 'offers')}
        >
          <Tag size={20} color={activeTab === 'offers' ? '#CC151E' : '#666'} />
          <span style={styles.navButtonText(activeTab === 'offers')}>
            Offers
          </span>
        </button>
        {/* <button
          onClick={() => setActiveTab('history')}
          style={styles.navButton(activeTab === 'history')}
        >
          <HistoryIcon />
          <span style={styles.navButtonText(activeTab === 'history')}>
            History
          </span>
        </button> */}
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    backgroundColor: 'white',
    minHeight: '100vh',
    paddingBottom: '80px',
    maxWidth: '500px',
    margin: '0 auto',
    position: 'relative',
  },
  header: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1000,
    marginTop: '10px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginLeft: '4px',
  },
  backButton: {
    cursor: 'pointer',
  },
  venueName: {
    margin: '0',
    fontSize: '23px',
    fontWeight: '600',
    color: '#333',
    textAlign: 'left',
    marginBottom: '5px',
    marginTop: '5px',
    marginLeft: '5px',
    flex: 1,
  },
  ratingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#22C55E',
    padding: '6px 8px',
    borderRadius: '8px',
    color: 'white',
    fontSize: '13px',
    marginRight: '15px',
  },
  ratingText: {
    fontWeight: '600',
    height: '15px',
    width: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1',
  },
  searchContainer: {
    position: 'sticky',
    top: '0',
    backgroundColor: 'white',
    zIndex: 1000,
    padding: '9px 16px',
    background: 'white',
  },
  searchBar: {
    backgroundColor: '#f9f9f9',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    padding: '5px 16px',
    gap: '12px',
    marginBottom: '6px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    border: '1px solid #eee',
  },
  searchInput: {
    border: 'none',
    background: 'none',
    fontSize: '15px',
    width: '100%',
    outline: 'none',
    color: '#333',
    fontWeight: '500',
  },
  clearSearch: {
    cursor: 'pointer',
  },
  filtersContainer: {
    display: 'flex',
    gap: '6px',
    marginTop: '12px',
    overflowX: 'auto',
    paddingBottom: '1px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    alignItems: 'center',
  },
  filterToggleContainer: {
    display: 'flex',
    gap: '12px',
  },
  filterToggle: (active, color) => ({
    width: '70px',
    height: '30px',
    backgroundColor: 'white',
    border: '1px solid #E0E0E0',
    borderRadius: '16px',
    padding: '4px',
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  }),
  itemTypeIndicator: {
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
  },
  vegIndicatorContainer: {
    width: '16px',
    height: '16px',
    border: '2px solid #4CAF50',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  vegIndicatorInner: {
    width: '8px',
    height: '8px',
    backgroundColor: '#4CAF50',
    borderRadius: '50%',
  },
  nonVegIndicatorContainer: {
    width: '16px',
    height: '16px',
    border: '2px solid #D32F2F',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nonVegIndicatorInner: {
    width: '0',
    height: '0',
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    borderBottom: '8px solid #D32F2F',
  },
  toggleTrack: (active, color) => ({
    width: '47px',
    height: '6px',
    backgroundColor: active ? color : '#E0E0E0',
    borderRadius: '19px',
    position: 'absolute',
    top: '11px',
    marginLeft: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  toggleThumb: active => ({
    width: '16px',
    height: '16px',
    border: '2px solid grey',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    position: 'absolute',
    left: active ? '36px' : '1px',
    transition: 'left 0.25s ease',
  }),
  vegIndicator: {
    width: '8px',
    height: '8px',
    backgroundColor: '#4CAF50',
    borderRadius: '1px ',
  },
  nonVegIndicator: {
    width: '0',
    height: '0',
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    borderBottom: '8px solid #D32F2F',
  },
  filterButton: (active, color) => ({
    padding: '4px 7px',
    borderRadius: '20px',
    border: `1px solid ${color}`,
    color: active ? 'white' : color,
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    fontWeight: '500',
    backgroundColor: active ? color : 'white',
    whiteSpace: 'nowrap',
  }),
  menuContent: {
    backgroundColor: '#fff',
    padding: '1px 16px',
    minHeight: 'calc(100vh - 300px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem', // equivalent to Tailwind's space-y-6 (24px)
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 4px',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    alignItems: 'flex-start',
    position: 'relative',
    borderRadius: '10px',
  },
  itemInfo: {
    display: 'flex',
    gap: '12px',
    flex: 1,
    alignItems: 'flex-start',
  },
  itemTypeIcon: {
    width: '16px',
    height: '16px',
    borderRadius: '3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemDetails: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  itemName: {
    margin: '0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    textAlign: 'left',
  },
  priceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
  },
  itemPrice: {
    margin: '0',
    fontSize: '15px',
    color: '#666',
  },
  tagsContainer: {
    display: 'flex',
    gap: '4px',
  },
  chefsSpecialTag: {
    display: 'flex',
    alignItems: 'center', // vertically center
    backgroundColor: '#D32F2F',
    color: 'white',
    fontSize: '10px',
    padding: '1px 6px',
    marginBottom: '1 px',
    borderRadius: '10px',
    fontWeight: '300',
  },
  itemActions: {
    marginLeft: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#22C55E',
    borderRadius: '20px',
    padding: '6px 12px',
    color: 'white',
    minWidth: '90px',
    justifyContent: 'center',
    height: '36px',
  },
  quantityButton: {
    cursor: 'pointer',
  },
  addButton: {
    backgroundColor: 'white',
    border: '1px solid #22C55E',
    color: '#22C55E',
    padding: '8px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    minWidth: '90px',
    height: '36px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  customizableLabel: {
    fontSize: '10px',
    color: '#666',
    marginTop: '2px',
    textAlign: 'center',
    width: '100%',
  },
  categoryContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '4px 6px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
    display: 'flex',
    flexDirection: 'column',
  },
  categoryTitle: {
    margin: '12px 0',
    fontSize: '19px',
    fontWeight: '700',
    color: '#CC151E',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 4px',
    borderRadius: '8px',
  },
  arrowIcon: {
    fontSize: '18px',
    marginRight: '10px',
    fontWeight: '500',
    color: '#CC151E',
  },

  noMenuText: {
    textAlign: 'center',
    color: '#888',
    marginTop: '20px',
  },
  noItemsText: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
    fontSize: '15px',
  },
  offersContainer: {
    padding: '16px',
  },
  offersTitle: {
    margin: '0 0 20px 0',
    fontSize: '20px',
    fontWeight: '700',
    color: '#CC151E',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  offerCard: {
    marginBottom: '16px',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
    border: '2px solid #eee',
  },
  offerHeader: {
    background: 'linear-gradient(90deg, #CC151E -18.35%, #873662 108.23%)',
    padding: '16px 20px',
    color: 'white',
  },
  offerTitle: {
    margin: '0',
    fontSize: '18px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  offerBody: {
    padding: '16px',
  },
  offerDescription: {
    margin: '0 0 16px 0',
    color: '#444',
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'left',
  },
  offerDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  offerValidUntil: {
    fontSize: '13px',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  noOffers: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
  },
  noOffersText: {
    fontSize: '15px',
    margin: 0,
  },
  cartSummary: {
    position: 'fixed',
    bottom: '60px',
    left: 0,
    right: 0,
    backgroundColor: '#22C55E',
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'white',
    zIndex: 1001,
    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
  },
  itemCount: {
    fontSize: '13px',
  },
  totalPrice: {
    fontSize: '15px',
    fontWeight: '600',
  },
  viewOrderButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.3)',
    padding: '6px 13px',
    borderRadius: '20px',
  },
  viewOrderText: {
    fontWeight: '600',
    fontSize: '14px',
  },
  bestsellerContainer: {
    background: 'linear-gradient(135deg, #FFF8F0 0%, #FFEFE6 100%)',
    border: '2px solid rgba(255, 107, 53, 0.3)',
    animation: 'pulse 2s infinite',
  },

  chefsSpecialContainer: {
    background: 'linear-gradient(135deg, #F8F0FF 0%, #F0E6FF 100%)',
    border: '2px solid rgba(138, 43, 226, 0.3)',
    animation: 'pulse 2s infinite',
  },

  // Corner ribbon styles
  ribbonCorner: {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '80px',
    height: '80px',
    overflow: 'hidden',
    zIndex: 3,
  },

  bestsellerRibbon: {
    position: 'absolute',
    top: '16px',
    right: '-20px',
    width: '100px',
    height: '20px',
    backgroundColor: '#FF6B35',
    background: 'linear-gradient(45deg, #FF6B35, #FF8A65)',
    color: 'white',
    fontSize: '8px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    transform: 'rotate(45deg)',
    boxShadow: '0 2px 8px rgba(255, 107, 53, 0.4)',
    letterSpacing: '0.5px',
  },

  chefsSpecialRibbon: {
    position: 'absolute',
    top: '16px',
    right: '-20px',
    width: '100px',
    height: '20px',
    backgroundColor: '#8A2BE2',
    background: 'linear-gradient(45deg, #8A2BE2, #9966CC)',
    color: 'white',
    fontSize: '8px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    transform: 'rotate(45deg)',
    boxShadow: '0 2px 8px rgba(138, 43, 226, 0.4)',
    letterSpacing: '0.5px',
  },

  ribbonText: {
    fontSize: '7px',
    fontWeight: '900',
    letterSpacing: '0.3px',
  },

  // Gradient overlays
  bestsellerOverlay: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background:
      'linear-gradient(45deg, rgba(255, 107, 53, 0.02) 0%, rgba(255, 138, 101, 0.02) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
  },

  chefsSpecialOverlay: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background:
      'linear-gradient(45deg, rgba(138, 43, 226, 0.02) 0%, rgba(153, 102, 204, 0.02) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
  },

  // Enhanced item name container
  itemNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    position: 'relative',
  },

  // Enhanced floating tags
  floatingTagsContainer: {
    display: 'flex',
    gap: '6px',
  },

  enhancedBestsellerTag: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'orange',
    color: 'white',
    fontSize: '10px',
    padding: '4px 8px',
    borderRadius: '12px',
    fontWeight: '700',
    boxShadow:
      '0 4px 12px rgba(255, 107, 53, 0.3), 0 2px 4px rgba(255, 107, 53, 0.2)',
    transform: 'scale(1)',
    animation: 'float 3s ease-in-out infinite',
    overflow: 'hidden',
  },

  enhancedChefsSpecialTag: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'linear-gradient(135deg, #8A2BE2 0%, #9966CC 100%)',
    color: 'white',
    fontSize: '9px',
    padding: '4px 8px',
    borderRadius: '12px',
    fontWeight: '700',
    boxShadow:
      '0 4px 12px rgba(138, 43, 226, 0.3), 0 2px 4px rgba(138, 43, 226, 0.2)',
    transform: 'scale(1)',
    animation: 'float 3s ease-in-out infinite reverse',
    overflow: 'hidden',
  },

  tagGlow: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    animation: 'glow 2s ease-in-out infinite alternate',
  },

  tagShine: {
    position: 'absolute',
    top: '0',
    left: '-100%',
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    animation: 'shine 2s ease-in-out infinite',
  },

  tagText: {
    fontSize: '8px',
    fontWeight: '800',
    letterSpacing: '0.3px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },

  // Enhanced special description
  specialDescriptionContainer: {
    marginTop: '8px',
    padding: '6px 10px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },

  specialDescription: {
    fontSize: '11px',
    fontWeight: '600',
    fontStyle: 'italic',
    margin: '0',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  },

  // Enhanced quantity controls
  bestsellerQuantityControl: {
    background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A65 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
    border: 'none',
  },

  chefsSpecialQuantityControl: {
    background: 'linear-gradient(135deg, #8A2BE2 0%, #9966CC 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(138, 43, 226, 0.3)',
    border: 'none',
  },

  // Enhanced add buttons
  bestsellerAddButton: {
    background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A65 100%)',
    color: 'white',
    border: 'none',
    fontWeight: '700',
    fontSize: '12px',
    padding: '10px 16px',
    borderRadius: '12px',
    boxShadow:
      '0 4px 16px rgba(255, 107, 53, 0.3), 0 2px 8px rgba(255, 107, 53, 0.2)',
    transform: 'scale(1)',
    transition: 'all 0.2s ease',
  },

  chefsSpecialAddButton: {
    background: 'linear-gradient(135deg, #8A2BE2 0%, #9966CC 100%)',
    color: 'white',
    border: 'none',
    fontWeight: '700',
    fontSize: '12px',
    padding: '10px 16px',
    borderRadius: '12px',
    boxShadow:
      '0 4px 16px rgba(138, 43, 226, 0.3), 0 2px 8px rgba(138, 43, 226, 0.2)',
    transform: 'scale(1)',
    transition: 'all 0.2s ease',
  },
  menuButton: {
    position: 'fixed',
    bottom: '130px',
    right: '20px',
    background: 'linear-gradient(90deg, #CC151E -18.35%, #873662 108.23%)',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    cursor: 'pointer',
    zIndex: 1002,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  menuButtonText: {
    fontSize: '8px',
    marginTop: '1px',
  },
  categoriesMenu: {
    position: 'fixed',
    bottom: '70px',
    right: '2px',
    backgroundColor: 'rgba(255, 255, 255, 1)',
    zIndex: 1007,
    borderRadius: '12px',
    width: '250px',
    height: '320px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  categoriesMenuContent: {
    backgroundColor: 'rgba(255, 255, 255, 0.83)',
    height: '100%',
    width: '100%',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    color: '#1a1a1a',
  },
  categoriesMenuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    borderBottom: '1px solid #eee',
  },
  categoriesMenuTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    textAlign: 'right',
    color: '#CC151E',
  },
  closeCategoriesMenu: {
    cursor: 'pointer',
  },
  categoriesList: {
    overflowY: 'auto',
    flex: 1,
    padding: '12px',
  },
  categoryMatchBadge: {
    marginLeft: '8px',
    backgroundColor: '#4CAF50',
    color: 'white',
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 'bold',
    verticalAlign: 'middle',
  },
  categoryMenuItem: {
    padding: '10px 10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'left',
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#333',
    transition: 'background-color 0.2s ease',
  },
  categoryItemCount: {
    color: '#666',
    fontSize: '12px',
  },
  drinkDotContainer: {
    position: 'relative',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  drinkDot: {
    position: 'absolute',
    left: '0',
    top: '22px',
    width: '8px',
    height: '8px',
    backgroundColor: '#CC151E',
    borderRadius: '50%',
    boxShadow: '0 0 0 2px rgba(204, 21, 30, 0.2)',
  },
  priceTagsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
  },
  bestsellerTag: {
    backgroundColor: '#FF9800',
    color: 'white',
    fontSize: '10px',
    padding: '1px 6px',
    borderRadius: '10px',
    fontWeight: '300',
  },
  customizationModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    zIndex: 1003,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  customizationContent: {
    backgroundColor: 'white',
    width: '100%',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    padding: '24px 16px',
    maxHeight: '80vh',
    overflowY: 'auto',
    zIndex: 1004,
  },
  customizationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  customizationTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#CC151E',
  },
  closeCustomization: {
    cursor: 'pointer',
  },
  customizationOptions: {
    marginBottom: '20px',
  },
  optionGroup: {
    marginBottom: '20px',
  },
  optionGroupTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
  },
  optionItem: selected => ({
    padding: '12px',
    border: selected ? '2px solid #22C55E' : '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  }),
  optionInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
    marginRight: '12px',
  },
  optionName: {
    fontWeight: '500',
  },
  optionPrice: {
    fontWeight: '600',
  },
  optionSelectedIndicator: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#22C55E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionSelectedDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'white',
  },
  optionCheckboxSelected: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    backgroundColor: '#22C55E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
  },
  customizationGroup: {
    marginBottom: '20px',
  },
  customizationFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderTop: '1px solid #eee',
  },
  totalLabel: {
    fontSize: '16px',
    fontWeight: '500',
  },
  totalAmount: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#CC151E',
  },
  addToCartButton: {
    padding: '12px 24px',
    backgroundColor: '#22C55E',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-around',
    padding: '8px 0',
    zIndex: 1000,
    boxShadow: '0 -2px 6px rgba(0,0,0,0.05)',
  },
  navButton: active => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: active ? '#CC151E' : '#666',
    fontWeight: active ? '600' : '400',
  }),
  navButtonText: active => ({
    fontSize: '12px',
    marginTop: '4px',
    color: active ? '#CC151E' : '#666',
  }),
};
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulse {
    0% { transform: translateY(-2px) scale(1); }
    50% { transform: translateY(-4px) scale(1.01); }
    100% { transform: translateY(-2px) scale(1); }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-2px) rotate(1deg); }
  }
  
  @keyframes glow {
    0% { opacity: 0.5; }
    100% { opacity: 1; }
  }
  
  @keyframes shine {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;
document.head.appendChild(styleSheet);
export default VenuePage;
